import { useCallback } from 'react'
import { fetchWithAuth } from '@/lib/fetchWithAuth'
import type { ConversationSummary, ConversationMessage } from '@/types/api'

const API_BASE = '/api'

export function useConversations() {
  const loadConversations = useCallback(async (): Promise<ConversationSummary[]> => {
    const response = await fetchWithAuth(`${API_BASE}/conversations`)
    if (!response.ok) throw new Error(`Failed to load conversations: ${response.status}`)
    return response.json()
  }, [])

  const createConversation = useCallback(
    async (agentId?: string | null, title?: string): Promise<ConversationSummary> => {
      const response = await fetchWithAuth(`${API_BASE}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: agentId ?? null, title: title ?? 'New conversation' }),
      })
      if (!response.ok) throw new Error(`Failed to create conversation: ${response.status}`)
      return response.json()
    },
    []
  )

  const updateConversation = useCallback(
    async (id: string, patch: { title?: string; agent_id?: string | null }): Promise<ConversationSummary> => {
      const response = await fetchWithAuth(`${API_BASE}/conversations/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      if (!response.ok) throw new Error(`Failed to update conversation: ${response.status}`)
      return response.json()
    },
    []
  )

  const deleteConversation = useCallback(async (id: string): Promise<void> => {
    const response = await fetchWithAuth(
      `${API_BASE}/conversations/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    if (!response.ok) throw new Error(`Failed to delete conversation: ${response.status}`)
  }, [])

  const loadMessages = useCallback(
    async (id: string): Promise<ConversationMessage[]> => {
      const response = await fetchWithAuth(
        `${API_BASE}/conversations/${encodeURIComponent(id)}/messages`
      )
      if (!response.ok) throw new Error(`Failed to load messages: ${response.status}`)
      return response.json()
    },
    []
  )

  const appendMessage = useCallback(
    async (
      conversationId: string,
      role: 'user' | 'assistant',
      content: Record<string, unknown>
    ): Promise<ConversationMessage> => {
      const response = await fetchWithAuth(
        `${API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role, content }),
        }
      )
      if (!response.ok) throw new Error(`Failed to append message: ${response.status}`)
      return response.json()
    },
    []
  )

  return {
    loadConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    loadMessages,
    appendMessage,
  }
}
